'use client';

import type { ProgressStepStatus } from '@polyseme/design-system';

import { ProgressStepper } from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { Typography } from './typography';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const stepsKeys = [
  'form',
  'documents',
  'signature',
  'validation',
  'subscription',
] as const;

export const ProgressStepperDemo = () => {
  const t = useTranslations('landing.polyseme.testimonials');
  const [currentStep, setCurrentStep] = useState(0);
  const [paused, setPaused] = useState(false);

  const stepsConfig = stepsKeys.map((key) => ({
    key,
    label: t(`steps.${key}.label`),
    description: t(`steps.${key}.description`),
  }));

  const generateSteps = (
    current: number
  ): { label: string; status: ProgressStepStatus }[] => {
    return stepsConfig.map((step, idx) => {
      if (idx < current) return { label: step.label, status: 'complete' };
      if (idx === current) return { label: step.label, status: 'waiting' };
      return { label: step.label, status: 'idle' };
    });
  };

  useEffect(() => {
    if (paused) return;

    const timer = setInterval(() => {
      setCurrentStep((prev) => (prev < stepsConfig.length - 1 ? prev + 1 : 0));
    }, 2_500);

    return () => clearInterval(timer);
  }, [paused, currentStep, stepsConfig.length]);

  const steps = generateSteps(currentStep);
  const activeStep = stepsConfig[currentStep];

  return (
    <motion.div
      className="flex flex-col items-center gap-10 px-4"
      initial="hidden"
      transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
      variants={fadeInUp}
      viewport={{ once: true, amount: 0.3 }}
      whileInView="visible"
    >
      <div className="w-full max-w-2xl">
        <ProgressStepper steps={steps} />
      </div>

      <div
        aria-live="polite"
        className="mt-6 size-full min-h-44 max-w-2xl rounded-3xl bg-muted p-8 text-sm sm:min-h-36"
      >
        <Typography as="h3" className="block text-base font-medium">
          {activeStep?.label}
        </Typography>
        <Typography className="mt-1 text-base text-muted-foreground">
          {activeStep?.description}
        </Typography>
      </div>

      <div aria-hidden className="flex gap-4">
        <div
          aria-label={t('demo.controls.stepper')}
          className="flex gap-2 rounded-[60px] bg-gray-100 px-4 py-5"
          role="tablist"
        >
          {stepsConfig.map((step, index) => (
            <button
              key={step.key}
              aria-label={t('demo.controls.goToStep', {
                step: String(index + 1),
                label: step.label,
              })}
              aria-selected={index === currentStep}
              className={cn(
                'relative h-2 w-2 rounded-full bg-white transition-all duration-300',
                { 'w-8 bg-gray-700': index === currentStep }
              )}
              role="tab"
              onClick={() => setCurrentStep(index)}
            >
              {index === currentStep && !paused && (
                <motion.div
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.8, 0, 0.8],
                  }}
                  className="absolute inset-0 rounded-full bg-gray-700"
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              )}
            </button>
          ))}
        </div>

        <button
          aria-label={
            paused ? t('demo.controls.resume') : t('demo.controls.pause')
          }
          className="flex size-12 items-center justify-center rounded-full bg-gray-100"
          onClick={() => setPaused((p) => !p)}
        >
          {paused ? (
            <div
              className="ml-1 size-5 bg-gray-700"
              style={{
                clipPath: 'polygon(20% 10%, 20% 90%, 85% 50%)',
                borderRadius: '2px',
              }}
            />
          ) : (
            <>
              {Array.from({ length: 2 }).map((_, idx) => (
                <div
                  key={idx}
                  className="mx-0.5 h-4 w-1.5 rounded-full bg-gray-700"
                />
              ))}
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};
